import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type DragEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/http';
import type { Attachment, Message } from '../api/types';
import { Avatar } from '../components/Avatar';
import { IconBack, IconDownload, IconClose, IconPhone, IconVideo } from '../components/Icons';
import { formatDayLabel, sameDay } from '../lib/format';
import { closeConversation, loadOlder, openConversation, sendFiles, setReplyDraft, toggleReaction, useChat, type MessageItem } from '../store/chat';
import { startCall, useCall } from '../calls/callManager';
import { PeerProfile } from './PeerProfile';
import { MessageBubble, type MenuAnchor } from './MessageBubble';
import { MessageMenu } from './MessageMenu';
import { ForwardPicker } from './ForwardPicker';
import { Composer } from './Composer';

const EMPTY: Message[] = [];
const EMPTY_PENDING: MessageItem[] = [];

function Lightbox({ a, onClose }: { a: Attachment; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="lightbox" onClick={onClose} role="dialog" aria-label={a.fileName}>
      <div className="lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <span>{a.fileName}</span>
        <a className="icon-btn" href={`${a.url}?download`} download={a.fileName} aria-label="Скачать">
          <IconDownload />
        </a>
        <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
          <IconClose />
        </button>
      </div>
      <img src={a.url} alt={a.fileName} onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

export function ChatView() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const me = useChat((s) => s.me);
  const conv = useChat((s) => s.conversations[id]);
  const messages = useChat((s) => s.messages[id]) ?? EMPTY;
  const pending = useChat((s) => s.pending[id]) ?? EMPTY_PENDING;
  const hasMore = useChat((s) => s.hasMore[id]);
  const loaded = useChat((s) => s.loaded[id]);
  const callBusy = useCall((s) => s.phase !== 'idle' && s.phase !== 'ended');
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState<Attachment | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [menu, setMenu] = useState<{ m: MessageItem; anchor: MenuAnchor } | null>(null);
  const [forwarding, setForwarding] = useState<MessageItem | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Scroll state lives in refs: it changes on every scroll event and must not re-render.
  const stick = useRef(true); // the user is at the bottom: follow new messages
  const anchor = useRef<{ key: string; offset: number } | null>(null); // first visible message otherwise
  const loadingOlder = useRef(false);
  const [showJump, setShowJump] = useState(false);
  const [composerH, setComposerH] = useState(64);

  const onToggleReaction = useCallback((m: MessageItem, emoji: string) => void toggleReaction(m.conversationId, m.id, emoji), []);
  const onOpenMenu = useCallback((m: MessageItem, a: MenuAnchor) => {
    setMenu({ m, anchor: a });
  }, []);
  const closeMenu = useCallback(() => setMenu(null), []);
  const onReply = useCallback((m: MessageItem) => setReplyDraft(m.conversationId, m), []);

  /** Brings a quoted message into view and flashes it (when it is in the loaded history). */
  const jumpTo = useCallback((messageId: string) => {
    const el = listRef.current;
    const m = useChat.getState().messages[id]?.find((x) => x.id === messageId);
    const node = m && el?.querySelector<HTMLElement>(`[data-key="${CSS.escape(m.clientId)}"]`);
    if (!node) return;
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    node.classList.remove('flash');
    void node.offsetWidth; // restart the animation
    node.classList.add('flash');
  }, [id]);

  // A long press must not start iOS text selection on a message (the menu has "Copy").
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onSelectStart = (e: Event) => {
      const t = e.target instanceof Element ? e.target : (e.target as Node | null)?.parentElement;
      if (t?.closest('.bubble')) e.preventDefault();
    };
    el.addEventListener('selectstart', onSelectStart);
    return () => el.removeEventListener('selectstart', onSelectStart);
  }, []);

  useEffect(() => {
    setNotFound(false);
    setMenu(null);
    openConversation(id).catch((err) => err instanceof ApiError && err.status === 404 && setNotFound(true));
    return () => closeConversation(id);
  }, [id]);

  /** Remembers which message is at the top of the viewport and where. */
  const captureAnchor = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    for (const node of el.querySelectorAll<HTMLElement>('[data-key]')) {
      const r = node.getBoundingClientRect();
      if (r.bottom > top) {
        anchor.current = { key: node.dataset.key!, offset: r.top - top };
        return;
      }
    }
  }, []);

  /**
   * Scroll anchoring done by hand: Safari has no CSS `overflow-anchor`, so when content above the viewport
   * changes (older page prepended, an image finishes loading) the view would jump. After any change we either
   * stay at the bottom or put the anchored message back exactly where it was.
   */
  const restore = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    if (stick.current) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    const a = anchor.current;
    const node = a && el.querySelector<HTMLElement>(`[data-key="${CSS.escape(a.key)}"]`);
    if (!a || !node) return;
    const delta = node.getBoundingClientRect().top - el.getBoundingClientRect().top - a.offset;
    if (Math.abs(delta) >= 1) el.scrollTop += delta;
  }, []);

  useLayoutEffect(restore, [messages, pending, restore]);

  // Content grows when images/videos load; the viewport shrinks when the keyboard opens.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => restore());
    if (listRef.current) ro.observe(listRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [restore]);

  const onScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stick.current = fromBottom < 40;
    setShowJump(fromBottom > 600);
    captureAnchor();
    if (el.scrollTop < 400 && hasMore && !loadingOlder.current) {
      loadingOlder.current = true;
      loadOlder(id)
        .catch(() => {})
        .finally(() => (loadingOlder.current = false));
    }
  }, [hasMore, id, captureAnchor]);

  const jumpToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    stick.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = [...e.dataTransfer.files];
    if (files.length) void sendFiles(id, files);
  };

  if (notFound) {
    return (
      <section className="chat">
        <div className="empty center">
          Диалог не найден. <Link to="/">К списку чатов</Link>
        </div>
      </section>
    );
  }

  const items: MessageItem[] = [...messages, ...pending];

  return (
    <section
      className="chat"
      style={{ '--composer-h': `${composerH}px` } as CSSProperties}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
      onDrop={onDrop}
    >
      <header className="chat-header">
        <button className="icon-btn back" onClick={() => nav('/')} aria-label="Назад">
          <IconBack />
        </button>
        {conv && (
          <button className="chat-peer" onClick={() => setShowProfile(true)}>
            <Avatar user={conv.peer} size={38} />
            <span className="chat-peer-text">
              <span className="chat-peer-name">{conv.peer.displayName}</span>
              {conv.peer.username && <span className="chat-peer-sub">@{conv.peer.username}</span>}
            </span>
          </button>
        )}
        <div className="spacer" />
        {conv && (
          <>
            <button className="icon-btn" disabled={callBusy} onClick={() => void startCall(conv, 'audio')} aria-label="Аудиозвонок">
              <IconPhone />
            </button>
            <button className="icon-btn" disabled={callBusy} onClick={() => void startCall(conv, 'video')} aria-label="Видеозвонок">
              <IconVideo />
            </button>
          </>
        )}
      </header>

      <div className="messages" ref={listRef} onScroll={onScroll}>
        <div className="messages-content" ref={contentRef}>
        {hasMore && <div className="loading-older">Загрузка…</div>}
        {!loaded && items.length === 0 && <div className="empty center">Загрузка…</div>}
        {loaded && items.length === 0 && <div className="empty center">Напишите первое сообщение</div>}
        {items.map((m, i) => {
          const prev = items[i - 1];
          return (
            <Fragment key={m.clientId}>
              {(!prev || !sameDay(prev.createdAt, m.createdAt)) && (
                <div className="day-sep">
                  <span>{formatDayLabel(m.createdAt)}</span>
                </div>
              )}
              <MessageBubble
                m={m}
                mine={m.senderId === me?.id}
                conv={conv}
                myId={me?.id}
                onOpenImage={setLightbox}
                onToggleReaction={onToggleReaction}
                onOpenMenu={onOpenMenu}
                onJumpTo={jumpTo}
              />
            </Fragment>
          );
        })}
        </div>
      </div>
      {showJump && (
        <button className="jump-bottom" onClick={jumpToBottom} aria-label="К последним сообщениям">
          <IconBack size={20} style={{ transform: 'rotate(-90deg)' }} />
        </button>
      )}

      <Composer conversationId={id} onHeight={setComposerH} />

      {menu && (
        <MessageMenu
          // The latest version of the message, so the highlighted reaction follows realtime updates.
          m={messages.find((x) => x.id === menu.m.id) ?? menu.m}
          anchor={menu.anchor}
          mine={menu.m.senderId === me?.id}
          myId={me?.id}
          onToggle={onToggleReaction}
          onReply={onReply}
          onForward={setForwarding}
          onClose={closeMenu}
        />
      )}
      {forwarding && (
        <ForwardPicker
          m={forwarding}
          authorName={forwarding.senderId === me?.id ? (me?.displayName ?? '') : (conv?.peer.displayName ?? '')}
          onClose={() => setForwarding(null)}
        />
      )}
      {dragging && <div className="drop-overlay">Отпустите, чтобы отправить файлы</div>}
      {lightbox && <Lightbox a={lightbox} onClose={() => setLightbox(null)} />}
      {showProfile && conv && <PeerProfile conv={conv} onClose={() => setShowProfile(false)} />}
    </section>
  );
}
